import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Building2Icon,
  LockIcon,
  SendIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react";
import type { JSONContent } from "@tiptap/react";

import { CandidateCommsTabs } from "@/components/user-sheet/comms/CandidateCommsTabs";
import { CommsPanelCollapseHeader } from "@/components/user-sheet/comms/CommsPanelCollapseHeader";
import { ExternalCommsTabs } from "./ExternalCommsTabs";
import { InternalCommsTabs } from "./InternalCommsTabs";

import { Center, Column, Row, Text } from "@/components";
import type { SendEmailComposerValues } from "@jobs/emails/EmailComposer";
import { Button } from "@/components/ui/button";

import { useCommsCandidacy } from "@/components/user-sheet/comms/useCommsCandidacy";
import { CommsScope } from "@/components/user-sheet/types";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useIsUserInAllowedList } from "@/hooks/useIsUserInAllowedList";
import { useGetEmails } from "@/hooks/queries/useGetEmails";
import { useGetQuoMessages } from "@/hooks/queries/useGetQuoMessages";
import { useIsMobile } from "@/hooks/use-mobile";
import { tiptapDefaultJson } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

const MAX_DISPLAY_CANDIDATE_COMMS_COUNT = 9;
// The rail's own width, not the viewport's — a `2xl:` breakpoint stays
// expanded on a wide monitor even when this panel itself is narrow.
// Compact (icon-only) is the intended default look; labels are the exception
// for an unusually wide rail, not the norm.
const SCOPE_LABEL_MIN_WIDTH_PX = 325;

const SCOPE_OPTIONS: { Icon: LucideIcon; label: string; value: CommsScope }[] =
  [
    { Icon: LockIcon, label: "Internal", value: CommsScope.INTERNAL },
    { Icon: SendIcon, label: "Candidate", value: CommsScope.CANDIDATE },
    { Icon: Building2Icon, label: "Company", value: CommsScope.COMPANY },
  ];

const scopeToActiveClassName: Record<CommsScope, string> = {
  [CommsScope.CANDIDATE]:
    "border-blue-800/20 bg-blue-200/70 text-blue-950 hover:bg-blue-200/70 hover:text-blue-950 dark:border-[#6F8FAA] dark:bg-[#527393] dark:text-[#FAFAFA] dark:hover:bg-[#355477] dark:hover:text-[#FAFAFA]",
  [CommsScope.COMPANY]:
    "border-slate-800/20 bg-slate-200 text-slate-950 hover:bg-slate-200 hover:text-slate-950 dark:border-[#77787A] dark:bg-[#77787A] dark:text-[#FAFAFA] dark:hover:bg-[#4C4F52] dark:hover:text-[#FAFAFA]",
  [CommsScope.INTERNAL]:
    "border-slate-700/20 bg-slate-100 text-slate-900 hover:bg-slate-100 hover:text-slate-900 dark:border-[#77787A] dark:bg-[#77787A] dark:text-[#FAFAFA] dark:hover:bg-[#4C4F52] dark:hover:text-[#FAFAFA]",
};

const scopeToPanelClassName: Record<CommsScope, string> = {
  [CommsScope.CANDIDATE]:
    "border-transparent bg-blue-100/70 dark:border-[#77787A] dark:bg-[#4C4F52]",
  [CommsScope.COMPANY]:
    "border-transparent bg-slate-200 dark:border-[#77787A] dark:bg-[#4C4F52]",
  [CommsScope.INTERNAL]:
    "border-slate-100 bg-slate-50 dark:border-[#77787A] dark:bg-[#4C4F52]",
};

// The candidacy sheet's comms rail panel: Internal / Candidate / Company scopes.
// Candidate is the shared Messages/Email pair; the other scopes fill in as the
// rollout progresses.
export const CandidateSheetCommsPanel = () => {
  /* Context */
  const {
    closeCommunications,
    communicationsRequestCount,
    communicationsRequestScope,
    communicationsScope,
    setCommunicationsScope,
  } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const commsCandidacy = useCommsCandidacy();
  const talentId = commsCandidacy?.talentId ?? null;
  // Same queries CandidateCommsTabs fetches, so the count and the tab content
  // share one cache entry instead of the panel re-requesting the same data.
  const messagesQuery = useGetQuoMessages({ talentId });
  const emailsQuery = useGetEmails({ talentId });

  /* React Hooks */
  const isCompanyScopeAllowed = useIsUserInAllowedList();
  const isMobile = useIsMobile();
  const scopeTabsRef = useRef<HTMLDivElement>(null);
  const [scopeTabsWidth, setScopeTabsWidth] = useState(0);
  // Drafts live here (above the scopes) so switching scopes/tabs doesn't
  // unmount the composers and lose what's been typed.
  const [internalCommentDraft, setInternalCommentDraft] =
    useState<JSONContent>(tiptapDefaultJson);
  const [externalCommentDraft, setExternalCommentDraft] =
    useState<JSONContent>(tiptapDefaultJson);
  const [messageDraft, setMessageDraft] = useState("");
  const [emailDraft, setEmailDraft] = useState<SendEmailComposerValues>({
    body: "",
    subject: "",
  });

  // Header actions like "Send email" target the candidate, so an open that names
  // no scope still jumps to the Candidate rail. Callers opening a tab that lives
  // elsewhere (a citation pointing at Internal comments) name their scope, and
  // that wins — otherwise this would immediately overwrite it.
  useEffect(() => {
    if (communicationsRequestCount === 0) return;
    setCommunicationsScope(communicationsRequestScope ?? CommsScope.CANDIDATE);
  }, [
    communicationsRequestCount,
    communicationsRequestScope,
    setCommunicationsScope,
  ]);

  useLayoutEffect(() => {
    const scopeTabsElement = scopeTabsRef.current;
    if (!scopeTabsElement) return;

    const measureScopeTabsWidth = () => {
      setScopeTabsWidth(scopeTabsElement.clientWidth);
    };

    measureScopeTabsWidth();
    const resizeObserver = new ResizeObserver(measureScopeTabsWidth);
    resizeObserver.observe(scopeTabsElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /* Helpers */
  const visibleScopeOptions = SCOPE_OPTIONS.filter(
    (option) => option.value !== CommsScope.COMPANY || isCompanyScopeAllowed,
  );
  // Scope lives in the sheet context so focus mode can save/restore it; a saved
  // value that no longer applies (e.g. Company without access) falls back.
  const scope =
    visibleScopeOptions.find((option) => option.value === communicationsScope)
      ?.value ?? CommsScope.INTERNAL;
  const shouldShowScopeLabels = scopeTabsWidth >= SCOPE_LABEL_MIN_WIDTH_PX;
  const candidateCommsCount =
    (messagesQuery.data?.length ?? 0) + (emailsQuery.data?.length ?? 0);
  const displayCandidateCommsCount =
    candidateCommsCount === 0
      ? null
      : candidateCommsCount > MAX_DISPLAY_CANDIDATE_COMMS_COUNT
        ? `${MAX_DISPLAY_CANDIDATE_COMMS_COUNT.toString()}+`
        : String(candidateCommsCount);

  /* Handlers */
  const handleScopeChange = (nextScope: CommsScope) => {
    setCommunicationsScope(nextScope);
    mixpanelUtils.trackTabView(MIXPANEL_SCREENS.CANDIDATE_SHEET, nextScope);
  };

  /* Render */
  const renderScopeTabs = () => {
    return (
      <Row
        className="w-full items-center gap-0.5 rounded-full bg-white px-1.5 py-1 shadow-inner"
        ref={scopeTabsRef}
      >
        {visibleScopeOptions.map(({ Icon, label, value }) => {
          const isActive = scope === value;

          return (
            <Button
              className={cn(
                "h-7 min-w-0 flex-1 rounded-full px-2 text-xs font-medium shadow-none hover:bg-transparent",
                shouldShowScopeLabels ? "gap-1.5 px-2.5" : "gap-1",
                isActive
                  ? cn("border shadow-sm", scopeToActiveClassName[value])
                  : "text-slate-500 hover:text-slate-700",
              )}
              key={value}
              onClick={() => {
                handleScopeChange(value);
              }}
              size="sm"
              title={label}
              type="button"
              variant="ghost"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {shouldShowScopeLabels ? (
                <Text className="text-xs font-medium text-inherit">
                  {label}
                </Text>
              ) : null}
              {value === CommsScope.CANDIDATE && displayCandidateCommsCount ? (
                <Center className="h-4 min-w-4 rounded-full bg-blue-200/70 px-1.5">
                  <Text className="text-[10px] font-normal text-blue-950">
                    {displayCandidateCommsCount}
                  </Text>
                </Center>
              ) : null}
            </Button>
          );
        })}
      </Row>
    );
  };

  const renderScopeContent = () => {
    if (scope === CommsScope.CANDIDATE) {
      // Candidacy scopes SMS/email + the status-transition/agent-takeover flow.
      return (
        <CandidateCommsTabs
          candidacy={commsCandidacy}
          emailDraft={emailDraft}
          messageDraft={messageDraft}
          onEmailDraftChange={setEmailDraft}
          onMessageDraftChange={setMessageDraft}
        />
      );
    }

    if (scope === CommsScope.INTERNAL) {
      return (
        <InternalCommsTabs
          commentDraft={internalCommentDraft}
          onCommentDraftChange={setInternalCommentDraft}
        />
      );
    }

    return (
      <ExternalCommsTabs
        commentDraft={externalCommentDraft}
        onCommentDraftChange={setExternalCommentDraft}
      />
    );
  };

  const render = () => {
    return (
      <Column
        className={cn(
          "min-h-0 min-w-0 flex-1 self-stretch overflow-hidden border p-4",
          isMobile
            ? "m-0 rounded-none border-0 bg-transparent"
            : cn("m-4 rounded-xl", scopeToPanelClassName[scope]),
        )}
      >
        <Column className="min-w-0 shrink-0 gap-2">
          <CommsPanelCollapseHeader />
          <Row className="min-w-0 items-center gap-2">
            <Column className="min-w-0 flex-1">{renderScopeTabs()}</Column>
            {isMobile ? (
              <Button
                className="h-12 w-12 shrink-0 text-slate-500 hover:bg-transparent hover:text-slate-500 [&_svg]:size-5"
                onClick={closeCommunications}
                size="icon"
                title="Collapse communications"
                type="button"
                variant="ghost"
              >
                <XIcon className="h-5 w-5" />
              </Button>
            ) : null}
          </Row>
        </Column>
        <Column className="-mx-2 min-h-0 min-w-0 flex-1 pt-2">
          {renderScopeContent()}
        </Column>
      </Column>
    );
  };

  return render();
};
